// Room variant mapping types for cross-OTA comparison

/**
 * Represents a mapping between internal room variant and OTA-specific room names
 */
export interface RoomVariantOTAMapping {
  id: string;
  internalVariantId: number; // References RoomVariant.id
  internalVariantName: string;
  internalRoomTypeId: number;
  internalRoomTypeName: string;
  otaMappings: OTARoomMapping[];
  createdAt: string;
  updatedAt: string;
}

/**
 * Individual OTA room mapping
 */
export interface OTARoomMapping {
  otaChannelId: string;
  otaChannelName: string;
  otaRoomName: string;
  otaRoomId?: string;
  matchConfidence: number; // 0-100, from fuzzy matching
  isManualOverride: boolean; // true if user manually set this mapping
  lastSynced?: string;
}

/**
 * Competitor's room variant as scraped from OTA
 */
export interface CompetitorOTARoomVariant {
  competitorId: string;
  otaChannelId: string;
  otaRoomName: string;
  otaRoomId?: string;
  price?: number;
  lastUpdated?: string;
}

/**
 * Fuzzy match suggestion for room variant mapping
 */
export interface FuzzyMatchSuggestion {
  internalVariantId: number;
  internalVariantName: string;
  otaRoomName: string;
  confidence: number;
  matchReason: string;
}

/**
 * Heatmap cell data for competitor pricing grid
 */
export interface CompetitorHeatmapCell {
  competitorId: string;
  competitorName: string;
  date: string;
  price: number;
  priceVsYours: number;
  priceVsYoursPercent: number;
  heatLevel: 'much-lower' | 'lower' | 'similar' | 'higher' | 'much-higher';
  otaChannel: string;
  roomVariant?: string;
}

/**
 * Full heatmap row (one per date)
 */
export interface CompetitorHeatmapRow {
  date: string;
  dayOfWeek: string;
  yourPrice: number;
  marketAvg: number;
  occupancy: number;
  competitorPrices: Record<string, CompetitorHeatmapCell>;
  suggestedPrice: number;
  suggestionStatus: 'pending' | 'accepted' | 'rejected';
}

/**
 * Calculate heat level based on price difference percentage
 */
export function getHeatLevel(priceVsYoursPercent: number): CompetitorHeatmapCell['heatLevel'] {
  if (priceVsYoursPercent <= -15) return 'much-lower';
  if (priceVsYoursPercent < -5) return 'lower';
  if (priceVsYoursPercent <= 5) return 'similar';
  if (priceVsYoursPercent <= 15) return 'higher';
  return 'much-higher';
}

/**
 * Heat level colors for the heatmap grid
 */
export const HEAT_LEVEL_COLORS: Record<CompetitorHeatmapCell['heatLevel'], { bg: string; text: string; border: string }> = {
  'much-lower': { bg: 'bg-red-100', text: 'text-red-700', border: 'border-red-300' },
  'lower': { bg: 'bg-orange-100', text: 'text-orange-700', border: 'border-orange-300' },
  'similar': { bg: 'bg-gray-100', text: 'text-gray-700', border: 'border-gray-300' },
  'higher': { bg: 'bg-green-100', text: 'text-green-700', border: 'border-green-300' },
  'much-higher': { bg: 'bg-emerald-100', text: 'text-emerald-700', border: 'border-emerald-300' },
};

/**
 * Simple fuzzy string matching for room variant names
 */
export function fuzzyMatch(str1: string, str2: string): number {
  const s1 = str1.toLowerCase().trim();
  const s2 = str2.toLowerCase().trim();
  
  if (s1 === s2) return 100;
  
  // Check if one contains the other
  if (s1.includes(s2) || s2.includes(s1)) {
    const shorter = s1.length < s2.length ? s1 : s2;
    const longer = s1.length < s2.length ? s2 : s1;
    return Math.round((shorter.length / longer.length) * 85);
  }
  
  // Token-based matching
  const tokens1 = s1.split(/[\s-_]+/);
  const tokens2 = s2.split(/[\s-_]+/);
  
  let matchedTokens = 0;
  for (const t1 of tokens1) {
    for (const t2 of tokens2) {
      if (t1 === t2 || t1.includes(t2) || t2.includes(t1)) {
        matchedTokens++;
        break;
      }
    }
  }
  
  const totalTokens = Math.max(tokens1.length, tokens2.length);
  return Math.round((matchedTokens / totalTokens) * 70);
}

/**
 * Get fuzzy match suggestions for a room variant
 */
export function getFuzzyMatchSuggestions(
  internalVariant: { id: number; name: string },
  otaRoomNames: string[]
): FuzzyMatchSuggestion[] {
  return otaRoomNames
    .map(otaName => {
      const confidence = fuzzyMatch(internalVariant.name, otaName);
      let matchReason = '';
      
      if (confidence >= 90) matchReason = 'Exact or near-exact match';
      else if (confidence >= 70) matchReason = 'Strong name similarity';
      else if (confidence >= 50) matchReason = 'Partial match - verify manually';
      else matchReason = 'Low confidence - manual mapping recommended';
      
      return {
        internalVariantId: internalVariant.id,
        internalVariantName: internalVariant.name,
        otaRoomName: otaName,
        confidence,
        matchReason,
      };
    })
    .sort((a, b) => b.confidence - a.confidence);
}
